import { Injectable } from '@angular/core';

import { Sensor } from 'src/app/shared/interfaces/sensor';
import {
  Site,
  SiteDrawingTiltSensorPosition,
} from 'src/app/shared/interfaces/site';
import { SensorService } from 'src/app/shared/services/sensor.service';
import { SiteService } from 'src/app/shared/services/site.service';

import { DrawingTiltSensor } from './drawing-tilt-sensor.interface';

/**
 * 山留めセンサー図面エディタ用サービス
 * 現場に設定されている山留めセンサー(TILT)の位置を図面上で管理
 */
@Injectable({
  providedIn: 'root',
})
export class DrawingTiltEditorService {
  constructor(
    private siteService: SiteService,
    private sensorService: SensorService
  ) {}

  /**
   * 現場に設定されている山留めセンサー情報を取得
   * 既存の図面位置情報があれば反映、なければデフォルト位置に配置
   */
  async getDrawingSensorsFromSite(site: Site): Promise<DrawingTiltSensor[]> {
    // 現場の全センサーから山留めセンサーのみ抽出
    let sensors: Sensor[] = await this.sensorService.getFromSiteId(site.id);
    sensors = sensors.filter((sensor) => sensor.categoryName === 'TILT');

    if (sensors.length === 0) {
      return [];
    }

    // 既存の図面位置情報を取得（drawingTiltプロパティから）
    const existingPositions = site.drawingTilt?.positions || [];
    const positionMap = new Map<string, SiteDrawingTiltSensorPosition>();
    existingPositions.forEach((pos) => {
      positionMap.set(pos.sensorId, pos);
    });

    // DrawingTiltSensor配列を構築
    const drawingSensors: DrawingTiltSensor[] = sensors.map((sensor, index) => {
      const existingPos = positionMap.get(sensor.id);
      const positionName = this.getPositionName(sensor);

      return {
        id: sensor.id,
        positionName: positionName,
        // 既存位置があればそれを使用、なければデフォルト位置(左側に縦並び)
        x: existingPos?.x ?? 30,
        y: existingPos?.y ?? 30 + index * 40,
      };
    });

    return drawingSensors;
  }

  /**
   * センサーの位置名称を決定
   * positionNameがあればそれを使用、なければpositionから推測
   */
  private getPositionName(sensor: Sensor): string {
    if (sensor.positionName) {
      return sensor.positionName;
    }

    // positionから推測(将来的な拡張のため残す)
    if (sensor.position) {
      // 必要に応じて変換ロジックを追加
      return sensor.position;
    }

    return `センサー${sensor.id.substring(0, 4)}`;
  }

  /**
   * 図面画像をCloud Storageにアップロード
   */
  async uploadDrawingImage(siteId: string, file: File): Promise<void> {
    await this.siteService.uploadDrawingTilt(siteId, file);
  }

  /**
   * 山留めセンサーの図面上の位置情報をFirestoreに保存
   */
  async updateDrawingPositions(
    siteId: string,
    drawingSensors: DrawingTiltSensor[]
  ): Promise<void> {
    const positions: SiteDrawingTiltSensorPosition[] = drawingSensors.map(
      (sensor) => ({
        sensorId: sensor.id,
        x: sensor.x,
        y: sensor.y,
      })
    );

    await this.siteService.updateDrawingTiltPositions(siteId, positions);
  }
}
